import React, { createContext, useContext, useState, useCallback, useRef, useEffect } from 'react';
import { checkPermissionStatus, permissionStateMessages, permissionStates, requestCameraAndMicPermission } from '@/utility/permission';
import { DialogModal } from '@/components';
import * as Icons from '@/assets/svg';
import '@/assets/styles/components/device-permission.styes.scss';

type PermissionStatus = {
	camera: string | null;
	microphone: string | null;
};

type DevicePermissionContextType = {
	hasPermission: boolean;
	controlPermission: (callback?: () => any) => Promise<void>;
};

const DevicePermissionContext = createContext<DevicePermissionContextType | undefined>(undefined);

const isInReactNativeWebView = () => {
	try {
		return typeof window !== 'undefined' && typeof window.nativePermissionBridge !== 'undefined' && typeof window.nativePermissionBridge.requestPermissions === 'function';
	} catch (error) {
		console.error('isInReactNativeWebView kontrol hatası:', error);
		return false;
	}
};

export const DevicePermissionProvider: React.FC<{
	children: React.ReactNode;
}> = ({ children }) => {
	const [isActivePermissionControl, setIsActivePermissionControl] = useState(false);
	const [hasPermission, setHasPermission] = useState(false);
	const [permissionPromptModal, setPermissionPromptModal] = useState(false);
	const [permissionDeniedModal, setPermissionDeniedModal] = useState(false);
	const [permissionDeviceStatus, setPermissionDeviceStatus] = useState<PermissionStatus>({
		camera: null,
		microphone: null
	});
	const refCallback = useRef<(() => any) | null>(null);
	const [isNative, setIsNative] = useState(false);

	useEffect(() => {
		const inNative = isInReactNativeWebView();
		setIsNative(inNative);
	}, []);

	useEffect(() => {
		if (!isInReactNativeWebView()) return;

		const handlePermissionResult = event => {
			try {
				const data = event.detail;

				if (data && data.permissions) {
					const { camera, microphone } = data.permissions;

					if (camera === 'granted' && microphone === 'granted') {
						setHasPermission(true);

						setPermissionDeniedModal(false);
						setPermissionPromptModal(false);

						if (refCallback.current) {
							refCallback.current();
							refCallback.current = null;
						}
					} else {
						setPermissionDeviceStatus({
							camera: camera === 'granted' ? permissionStateMessages.granted : permissionStateMessages.denied,
							microphone: microphone === 'granted' ? permissionStateMessages.granted : permissionStateMessages.denied
						});
					}
				}
			} catch (error) {
				console.error('Permission event işleme hatası:', error);
			}
		};

		window.addEventListener('native-permission-result', handlePermissionResult);

		return () => {
			window.removeEventListener('native-permission-result', handlePermissionResult);
		};
	}, []);

	useEffect(() => {
		if (isActivePermissionControl) {
			(async () => {
				const stream = await navigator.mediaDevices.getUserMedia({
					video: true,
					audio: true
				});

				if (stream) {
					setHasPermission(true);

					const tracks = stream?.getTracks?.();

					tracks.forEach(track => {
						track?.stop?.();
					});
				}
			})();
		}
	}, [isActivePermissionControl]);

	const controlPermission = useCallback(
		async (callback?: (() => any) | null) => {
			if (callback) {
				refCallback.current = callback;
			} else {
				refCallback.current = null;
			}

			if (isNative || isInReactNativeWebView()) {
				try {
					setIsActivePermissionControl(true);
					window.nativePermissionBridge.requestPermissions();
				} catch (error) {
					await handleWebPermissions(callback);
					console.log('Native permission request error:', error);
				}
				return;
			}

			await handleWebPermissions(callback);
		},
		[isNative]
	);

	const handleWebPermissions = async (callback?: (() => any) | null) => {
		const check = await checkPermissionStatus();
		if (check?.camera === permissionStates.granted && check?.microphone === permissionStates.granted) {
			setHasPermission(true);
			setPermissionDeniedModal(false);
			setPermissionPromptModal(false);
			if (callback) {
				callback();
				if (refCallback.current === callback) {
					refCallback.current = null;
				}
			}
		} else if (check?.microphone === permissionStates.prompt || check?.camera === permissionStates.prompt) {
			setPermissionPromptModal(true);
		} else {
			setPermissionDeniedModal(true);

			setPermissionDeviceStatus({
				camera: permissionStateMessages?.[check?.camera as keyof typeof permissionStateMessages] ?? permissionStateMessages.unknown,
				microphone: permissionStateMessages?.[check?.microphone as keyof typeof permissionStateMessages] ?? permissionStateMessages.unknown
			});
		}
	};

	const promptPermission = useCallback(async () => {
		if (isNative || isInReactNativeWebView()) {
			try {
				window.nativePermissionBridge.requestPermissions();
			} catch (error) {
				console.error('Native izin tekrar isteme hatası:', error);
			}
			setPermissionPromptModal(false);
			return;
		}

		const result = await requestCameraAndMicPermission();
		setPermissionPromptModal(false);

		if (result) {
			setHasPermission(true);
			if (refCallback?.current) {
				refCallback.current();
				refCallback.current = null;
			}
		} else {
			await controlPermission(refCallback?.current);
		}
	}, [controlPermission, isNative]);

	return (
		<DevicePermissionContext.Provider value={{ hasPermission, controlPermission }}>
			{children}

			{!isNative && (
				<>
					<DialogModal
						CustomIcon={
							<Icons.CameraAndMicrophone
								height={40}
								width={85}
							/>
						}
						isOpen={permissionPromptModal}
						onClose={() => setPermissionPromptModal(false)}
						variant="waiting"
						title="Mikrofonuna ve Kamerana İzin Ver"
						primaryButtonText="İzin Ver"
						onPrimaryClick={promptPermission}
						className={'permission-prompt-modal'}>
						<p>Görüşmeye katılmadan önce kameraya ve mikrofona erişim izni vermen gerekiyor.</p>
					</DialogModal>

					<DialogModal
						isOpen={permissionDeniedModal}
						onClose={() => setPermissionDeniedModal(false)}
						variant="waiting"
						title="İzinler Reddedildi"
						secondaryButtonText="Kontrol Et"
						className={'permission-denied-modal'}
						onSecondaryClick={() => controlPermission(refCallback?.current)}>
						<p>Görüşmeye katılmadan önce kamera ve mikrofona erişim izni gereklidir.</p>
						<p>Kamera: {permissionDeviceStatus.camera}</p>
						<p>Mikrofon: {permissionDeviceStatus.microphone}</p>
					</DialogModal>
				</>
			)}
		</DevicePermissionContext.Provider>
	);
};

export const useDevicePermissions = (): DevicePermissionContextType => {
	const context = useContext(DevicePermissionContext);
	if (!context) {
		throw new Error('useDevicePermissions must be used within a DevicePermissionProvider');
	}
	return context;
};

declare global {
	interface Window {
		nativePermissionBridge?: {
			requestPermissions: () => void;
			updatePermissionStatus: (data: any) => void;
		};
	}
}
